import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';
import { PENALTY_TYPE_LABEL, type Penalty, rm } from './penalties.js';

/**
 * 393 (#1313, US3, mock-up screen 5): a customer's receipt for one penalty. The receipt comes as a 10-minute link
 * (an image is shown, a PDF linked). Approve marks the penalty paid today, by you; Reject… needs a reason the customer
 * reads. Both send the receipt this dialog loaded (`if_uploaded_at`), so a receipt replaced meanwhile is refused.
 */
const REASON_MAX = 300;

export function PenaltyReceiptDialog({
  penalty,
  mayDecide,
  onClose,
}: {
  penalty: Penalty;
  /** 412 (#1505, mock-up frame 3): without confirm-payment the receipt is shown, not decided. */
  mayDecide: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const receipt = useQuery({
    queryKey: ['penalty-receipt', penalty.id, penalty.receipt_uploaded_at],
    queryFn: () =>
      api.get(`/penalties/${penalty.id}/receipt`) as Promise<{ url: string; mime: string }>,
    staleTime: 5 * 60_000,
  });
  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      api.post(
        `/penalties/${penalty.id}/receipt/${decision}`,
        decision === 'approve'
          ? { if_uploaded_at: penalty.receipt_uploaded_at }
          : { if_uploaded_at: penalty.receipt_uploaded_at, reason: reason.trim() },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penalties'] });
      onClose();
    },
  });

  return (
    <Modal open onClose={onClose} title="Check receipt" size="xl">
      <div className="flex flex-col gap-4 px-6 py-4 text-sm text-ink sm:flex-row">
        <div className="flex w-full shrink-0 items-center justify-center rounded-lg border border-hairline bg-surface sm:w-48">
          {receipt.isLoading && <Spinner />}
          {receipt.error && (
            <p className="p-3 text-semantic-danger-text">The receipt could not be loaded.</p>
          )}
          {receipt.data &&
            (receipt.data.mime === 'application/pdf' ? (
              <a
                href={receipt.data.url}
                target="_blank"
                rel="noreferrer"
                className="p-3 text-primary underline"
              >
                Open the PDF receipt
              </a>
            ) : (
              <a href={receipt.data.url} target="_blank" rel="noreferrer">
                <img src={receipt.data.url} alt="Payment receipt" className="max-h-72 rounded" />
              </a>
            ))}
        </div>
        <div className="flex flex-1 flex-col gap-3">
          <div>
            <p className="text-base font-semibold">
              {PENALTY_TYPE_LABEL[penalty.type] ?? penalty.type} · {rm(penalty.amount_minor)}
            </p>
            <p className="text-steel">
              {penalty.customer.name} · {penalty.car.plate_number} · booking {penalty.booking_ref}
            </p>
          </div>
          <p>
            Check that the receipt shows <strong>{rm(penalty.amount_minor)}</strong> paid to CARii.
          </p>
          {decide.isError && (
            <p role="alert" className="text-semantic-danger-text">
              {(decide.error as { message?: string }).message ?? 'The decision could not be saved.'}
            </p>
          )}
          {!mayDecide ? (
            <p data-testid="receipt-no-decision" className="text-steel">
              Only someone who may confirm customer payments can approve or reject this receipt.
            </p>
          ) : !rejecting ? (
            <div className="flex flex-wrap gap-2">
              <Button loading={decide.isPending} onClick={() => decide.mutate('approve')}>
                Approve: mark paid
              </Button>
              <Button variant="danger" onClick={() => setRejecting(true)}>
                Reject…
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <label htmlFor="receipt-reject-reason" className="font-medium">
                Why is it rejected? The customer reads this.
              </label>
              <Textarea
                id="receipt-reject-reason"
                rows={3}
                maxLength={REASON_MAX}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="text-right text-xs text-steel">
                {reason.length} / {REASON_MAX}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setRejecting(false)}>
                  Back
                </Button>
                <Button
                  variant="danger"
                  disabled={reason.trim().length === 0}
                  loading={decide.isPending}
                  onClick={() => decide.mutate('reject')}
                >
                  Reject receipt
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
